<template>
  <AnalyticsCard
    :title="spec.title"
    :slug="spec.slug"
    :loading="loading"
    :error="errorMessage"
    :pinned="pinned"
    :pinnable="pinnable"
    @retry="fetch"
    @pin="$emit('pin')"
  >
    <Chart type="bar" :data="chartData" :options="chartOptions" class="!h-full" />
  </AnalyticsCard>
</template>

<script setup lang="ts">
import 'chart.js/auto'
import { computed, ref, watch } from 'vue'
import Chart from 'primevue/chart'
import { useFrontegg } from '@frontegg/vue'
import AnalyticsCard from './AnalyticsCard.vue'
import { useFCCAnalytics } from '@/composables/useFCCAnalytics'
import {
  baseChartOptions,
  dualAxisScales,
  labeledTooltip,
} from '@/util/fcc/charts/chartTheme'
import { useThemeStore } from '@/stores/themeStore'
import type { ChartSpec } from '@/util/fcc/charts/chartSpecs'

interface Props {
  spec: ChartSpec
  merchantId: string
  dateFrom: string
  dateTo: string
  pinned?: boolean
  pinnable?: boolean
}

const props = defineProps<Props>()
defineEmits<{ pin: [] }>()

const frontegg = useFrontegg()
const themeStore = useThemeStore()
const analytics = useFCCAnalytics()
const data = ref<unknown>(null)
const errorMessage = ref<string | null>(null)
const loading = computed(() => analytics.loading.value)

const fetch = async () => {
  if (!props.dateFrom || !props.dateTo) return
  errorMessage.value = null
  try {
    data.value = await analytics.getSeries(props.spec.kind, frontegg.authState, {
      merchant_id: props.merchantId,
      date_from: props.dateFrom,
      date_to: props.dateTo,
    })
  } catch (err) {
    errorMessage.value = (err as Error)?.message ?? 'Unknown error'
  }
}

const chartData = computed(() => props.spec.buildData(data.value))

const chartOptions = computed(() => {
  const base = baseChartOptions(themeStore.isDark)
  return {
    ...base,
    plugins: {
      ...base.plugins,
      tooltip: {
        ...base.plugins?.tooltip,
        callbacks: labeledTooltip(...props.spec.tooltipMatchers),
      },
    },
    scales: dualAxisScales(
      base,
      props.spec.leftAxisFormat,
      props.spec.rightAxis,
      props.spec.leftStacked,
    ),
  }
})

watch(
  () => [props.spec.kind, props.merchantId, props.dateFrom, props.dateTo],
  () => void fetch(),
  { immediate: true },
)
</script>
